<template>
  <div
    class="space-y-3 rounded-2xl border border-[var(--ui-border)] bg-[var(--ui-bg-muted)]/25 p-3 sm:p-4"
    dir="rtl"
  >
    <div
      v-if="beforeSrc && afterSrc"
      class="grid grid-cols-2 gap-2"
    >
      <div class="relative overflow-hidden rounded-xl">
        <img
          :src="beforeSrc"
          alt="قبل"
          class="aspect-video w-full object-cover"
        >
        <span class="absolute bottom-2 start-2 rounded-md bg-black/60 px-2 py-1 text-xs font-medium text-white">قبل</span>
      </div>
      <div class="relative overflow-hidden rounded-xl">
        <img
          :src="afterSrc"
          alt="بعد"
          class="aspect-video w-full object-cover"
        >
        <span class="absolute bottom-2 start-2 rounded-md bg-black/60 px-2 py-1 text-xs font-medium text-white">بعد</span>
      </div>
    </div>
    <p
      v-else
      class="rounded-xl border border-dashed border-[var(--ui-border)] px-3 py-5 text-center text-sm text-[var(--ui-text-muted)]"
    >
      برای نمایش مقایسه، تصویر قبل و بعد را در تنظیمات بلوک اضافه کنید.
    </p>

    <UButton
      class="w-full justify-center"
      icon="i-lucide-images"
      :disabled="!beforeSrc || !afterSrc"
      @click="open = true"
    >
      مشاهدهٔ مقایسه
    </UButton>

    <div
      v-if="open"
      class="fixed inset-0 z-[100] flex items-stretch justify-center bg-black/80 sm:items-center sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-label="مقایسهٔ تصاویر قبل و بعد"
      @click.self="open = false"
      @keydown.esc="open = false"
    >
      <section class="flex h-[100dvh] w-full max-w-5xl flex-col bg-[var(--ui-bg)] sm:h-auto sm:max-h-[94dvh] sm:rounded-2xl">
        <header class="flex items-center justify-between gap-3 border-b border-[var(--ui-border)] px-4 py-3">
          <div>
            <h2 class="font-semibold">مقایسهٔ قبل و بعد</h2>
            <p class="mt-0.5 text-xs text-[var(--ui-text-muted)]">
              حالت نمایش را انتخاب کنید.
            </p>
          </div>
          <UButton
            icon="i-lucide-x"
            color="neutral"
            variant="ghost"
            aria-label="بستن مقایسه"
            @click="open = false"
          />
        </header>

        <div class="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          <div class="mx-auto max-w-4xl space-y-4">
            <div
              class="inline-flex rounded-xl bg-[var(--ui-bg-muted)] p-1"
              role="group"
              aria-label="حالت مقایسه"
            >
              <button
                v-for="item in modes"
                :key="item.value"
                type="button"
                class="rounded-lg px-4 py-2 text-sm font-medium transition"
                :class="mode === item.value
                  ? 'bg-[var(--arad-surface)] text-[var(--arad-text)] shadow-sm'
                  : 'text-[var(--ui-text-muted)] hover:text-[var(--arad-text)]'"
                :aria-pressed="mode === item.value"
                @click="mode = item.value"
              >
                {{ item.label }}
              </button>
            </div>

            <div
              ref="comparisonElement"
              class="relative aspect-[4/3] select-none overflow-hidden rounded-2xl bg-black sm:aspect-video"
            >
              <img
                :src="mode === 'fade' ? beforeSrc : afterSrc"
                :alt="mode === 'fade' ? 'قبل' : 'بعد'"
                draggable="false"
                class="absolute inset-0 size-full object-contain"
              >
              <img
                :src="mode === 'fade' ? afterSrc : beforeSrc"
                :alt="mode === 'fade' ? 'بعد' : 'قبل'"
                draggable="false"
                class="absolute inset-0 size-full object-contain"
                :style="mode === 'fade'
                  ? { opacity: position / 100 }
                  : { clipPath: `inset(0 ${100 - position}% 0 0)` }"
              >

              <div
                v-if="mode === 'slider'"
                class="absolute inset-y-0 z-10 w-0.5 -translate-x-1/2 bg-white shadow"
                :style="{ left: `${position}%` }"
                aria-hidden="true"
              />
              <div class="pointer-events-none absolute inset-x-0 top-0 flex justify-between p-3">
                <span class="rounded-lg bg-black/55 px-2.5 py-1 text-xs font-medium text-white">قبل</span>
                <span class="rounded-lg bg-black/55 px-2.5 py-1 text-xs font-medium text-white">بعد</span>
              </div>

              <div
                v-if="mode === 'slider'"
                role="slider"
                tabindex="0"
                aria-label="موقع جداکنندهٔ قبل و بعد"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="Math.round(position)"
                :aria-valuetext="`${Math.round(position)}٪`"
                class="absolute top-1/2 z-20 flex size-11 -translate-x-1/2 -translate-y-1/2 touch-none cursor-ew-resize items-center justify-center rounded-full border-2 border-white bg-black/55 text-white shadow-lg outline-none focus-visible:ring-4 focus-visible:ring-primary/50"
                :style="{ left: `${position}%` }"
                @pointerdown.prevent="startDrag"
                @pointermove="moveHandle"
                @pointerup="stopDrag"
                @pointercancel="stopDrag"
                @keydown.left.prevent="changePosition(-2)"
                @keydown.right.prevent="changePosition(2)"
                @keydown.home.prevent="position = 0"
                @keydown.end.prevent="position = 100"
              >
                <UIcon
                  name="i-lucide-move-horizontal"
                  class="size-5"
                />
              </div>
            </div>

            <input
              v-if="mode === 'fade'"
              v-model.number="position"
              type="range"
              min="0"
              max="100"
              :aria-label="'میزان نمایش تصویر بعد'"
              class="w-full accent-primary"
            >
            <p class="text-center text-xs text-[var(--ui-text-muted)]">
              {{ mode === 'slider' ? 'دستگیره را بکشید یا با کلیدهای جهت‌نما جابه‌جا کنید.' : 'لغزنده را برای تغییر تدریجی از قبل به بعد حرکت دهید.' }}
            </p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
withDefaults(defineProps<{
  beforeSrc: string
  afterSrc: string
}>(), {
  beforeSrc: '',
  afterSrc: '',
})

const modes = [
  { value: 'slider', label: 'کشویی' },
  { value: 'fade', label: 'محو تدریجی' },
] as const

const open = ref(false)
const mode = ref<(typeof modes)[number]['value']>('slider')
const position = ref(50)
const dragging = ref(false)
const comparisonElement = ref<HTMLElement | null>(null)

function startDrag(event: PointerEvent) {
  dragging.value = true
  const handle = event.currentTarget as HTMLElement
  handle.focus()
  handle.setPointerCapture(event.pointerId)
  updatePosition(event.clientX)
}

function moveHandle(event: PointerEvent) {
  if (dragging.value) updatePosition(event.clientX)
}

function stopDrag() {
  dragging.value = false
}

function updatePosition(clientX: number) {
  const rect = comparisonElement.value?.getBoundingClientRect()
  if (!rect?.width) return
  position.value = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100))
}

function changePosition(amount: number) {
  position.value = Math.max(0, Math.min(100, position.value + amount))
}
</script>
